import React, { useState } from "react";
import {
  Box, Typography, Paper, Button, TextField, Stack,
  IconButton, Tooltip, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  useGetDevices, useCreateDevice, useUpdateDevice, useToggleDevice, useDeleteDevice,
} from "./deviceQueries";

// ── Edit/Create Dialog ───────────────────────────────────────────────────────
const DeviceDialog = ({ open, onClose, initial }) => {
  const isEdit = Boolean(initial);
  const { mutate: create, isLoading: creating } = useCreateDevice();
  const { mutate: update, isLoading: updating } = useUpdateDevice();

  const [form, setForm] = useState(
    initial || { deviceId: "", name: "", location: "", os: "", osVersion: "", deviceType: "", manufacturer: "", model: "" }
  );

  React.useEffect(() => {
    setForm(initial || { deviceId: "", name: "", location: "", os: "", osVersion: "", deviceType: "", manufacturer: "", model: "" });
  }, [initial, open]);

  const handleSave = () => {
    if (
      !form.deviceId.trim() ||
      !form.name.trim() ||
      !form.location.trim() ||
      !form.os.trim() ||
      !form.deviceType.trim() ||
      !form.model.trim()
    )
      return;

    const payload = {
      deviceId: form.deviceId.trim(),
      name: form.name.trim(),
      location: form.location.trim(),
      os: form.os.trim(),
      osVersion: form.osVersion?.trim() || null,
      deviceType: form.deviceType.trim(),
      manufacturer: form.manufacturer?.trim() || null,
      model: form.model.trim(),
    };

    if (isEdit) {
      update({ id: initial.id, ...payload }, { onSuccess: onClose });
    } else {
      create(payload, { onSuccess: onClose });
    }
  };

  const isFormValid =
    form.deviceId.trim() &&
    form.name.trim() &&
    form.location.trim() &&
    form.os.trim() &&
    form.deviceType.trim() &&
    form.model.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit Device" : "Add Device"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Device ID"
            required
            fullWidth
            value={form.deviceId}
            onChange={(e) => setForm((p) => ({ ...p, deviceId: e.target.value }))}
          />
          <TextField
            label="Name"
            required
            fullWidth
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            label="Location"
            required
            fullWidth
            value={form.location}
            onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
          />
          <TextField
            label="OS"
            required
            fullWidth
            value={form.os}
            onChange={(e) => setForm((p) => ({ ...p, os: e.target.value }))}
          />
          <TextField
            label="OS Version (optional)"
            fullWidth
            value={form.osVersion || ""}
            onChange={(e) => setForm((p) => ({ ...p, osVersion: e.target.value }))}
          />
          <TextField
            label="Device Type"
            required
            fullWidth
            value={form.deviceType}
            onChange={(e) => setForm((p) => ({ ...p, deviceType: e.target.value }))}
          />
          <TextField
            label="Manufacturer (optional)"
            fullWidth
            value={form.manufacturer || ""}
            onChange={(e) => setForm((p) => ({ ...p, manufacturer: e.target.value }))}
          />
          <TextField
            label="Model"
            required
            fullWidth
            value={form.model}
            onChange={(e) => setForm((p) => ({ ...p, model: e.target.value }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={creating || updating || !isFormValid}
        >
          {creating || updating ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Delete Confirm Dialog ─────────────────────────────────────────────────────
const DeleteDialog = ({ open, onClose, device }) => {
  const { mutate: del, isLoading } = useDeleteDevice();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Device?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Are you sure you want to delete: <strong>{device?.name} ({device?.deviceId})</strong>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={isLoading}
          onClick={() => del(device.id, { onSuccess: onClose })}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Device Management Page ──────────────────────────────────────────────────
const ManageDevices = () => {
  const { data, isLoading } = useGetDevices();
  const { mutate: toggle } = useToggleDevice();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const devices = data?.data || [];

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (device) => { setEditing(device); setDialogOpen(true); };
  const closeDialog = () => setDialogOpen(false);

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", py: 4, px: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700} color="primary">
          Manage Devices
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Device
        </Button>
      </Stack>

      {isLoading && <Typography>Loading...</Typography>}

      {!isLoading && devices.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography color="text.secondary">No Devices yet. Click "Add Device" to create one.</Typography>
        </Paper>
      )}

      <Stack spacing={2}>
        {devices.map((device) => (
          <Paper
            key={device.id}
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              opacity: device.isActive ? 1 : 0.6,
              borderColor: device.isActive ? "grey.200" : "warning.light",
              bgcolor: device.isActive ? "white" : "grey.50",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Typography fontWeight={700} variant="body1">
                    {device.name}
                  </Typography>
                  {!device.isActive && (
                    <Chip label="Inactive" size="small" color="warning" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  <strong>ID:</strong> {device.deviceId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Location:</strong> {device.location}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>OS:</strong> {device.os}{device.osVersion ? ` (${device.osVersion})` : ""}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Device Type:</strong> {device.deviceType}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Model:</strong> {device.manufacturer ? `${device.manufacturer} ` : ""}{device.model}
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.5} flexShrink={0}>
                <Tooltip title={device.isActive ? "Deactivate" : "Activate"}>
                  <IconButton
                    size="small"
                    onClick={() => toggle(device.id)}
                    color={device.isActive ? "warning" : "success"}
                  >
                    {device.isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEdit(device)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => setDeleteTarget(device)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <DeviceDialog open={dialogOpen} onClose={closeDialog} initial={editing} />
      <DeleteDialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} device={deleteTarget} />
    </Box>
  );
};

export default ManageDevices;
